import React from 'react';
import styled from 'styled-components';
import { Box, Matrix, MatrixItem, Stack } from '@99designs/emu';
import { mediaQuery } from '@99designs/emu/css';
import { colors, spaces, typography } from '@99designs/emu/settings';
import { SelectableBox } from './SelectableBox';

interface Props {
    value: string;
    options: {
        value: string;
        title: React.ReactNode;
        label?: React.ReactNode;
        image?: string | null;
    }[];
    onChange: (value: string) => void;
    columns?: number;
}

const FullHeightBox = styled(Box)`
    height: 100%;
    min-height: 125px;
`;

const BoxInner = styled.div`
    padding: ${spaces['1bu']} ${spaces['2bu']};
    text-align: center;
    display: flex;
    flex-direction: column;
    justify-content: center;
    height: 100%;

    ${mediaQuery.only('small')} {
        padding: ${spaces['1bu']};
        height: auto;
    }
`;

const Image = styled.img`
    height: 75px;
    width: 75px;
    margin: auto;
    padding: ${spaces['0.5bu']};

    ${mediaQuery.only('small')} {
        height: 50px;
        width: 50px;
    }
`;

const Title = styled.div`
    font-family: ${typography.fontFamilies.heading};
    font-size: ${typography.fontSizes.h5.medium}px;
    color: ${colors.typography.root};
    line-height: ${typography.lineHeights.h5};
    margin-bottom: ${spaces['0.5bu']};

    ${mediaQuery.only('small')} {
        font-size: ${typography.fontSizes.h5.small}px;
    }
`;

const Label = styled.span`
    font-size: ${typography.fontSizes.paragraphs.small}px;
    color: ${colors.typography.soft};
`;

export default function BoxSelect({ options, value, onChange, columns }: Props) {
    const maxColumns = columns ?? 2;

    return (
        <Matrix columns={[1, maxColumns > 2 ? 2 : maxColumns, maxColumns]} space="2bu">
            {options.map((option) => (
                <MatrixItem key={option.value}>
                    <FullHeightBox color="grey800">
                        <SelectableBox
                            data-testid="selectable-box"
                            selected={option.value === value}
                            onClick={() => onChange(option.value)}
                        >
                            <BoxInner>
                                <Stack space="1bu">
                                    {option.image && <Image src={option.image} alt="" />}
                                    <div>
                                        <Title>{option.title}</Title>
                                        <Label>{option.label}</Label>
                                    </div>
                                </Stack>
                            </BoxInner>
                        </SelectableBox>
                    </FullHeightBox>
                </MatrixItem>
            ))}
        </Matrix>
    );
}
